import React from 'react';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import InputAdornment from '@mui/material/InputAdornment';
import Autocomplete from '@mui/material/Autocomplete';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';

import algolia from '../utilities/algolia';
import { StateManager } from '../utilities/stateManager';
import { StackedDialog, useSelectMenuProps, useAutocompleteSlotProps } from '../utilities/ModalStackContext';
import { applyIncentive, fetchActiveFinanceInvoice } from '../utilities/financeInvoice';

const USD = (v) =>
  (v || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });

const newRow = () => ({ key: Math.random().toString(36).slice(2), car: null, memo: '', amount: '' });

const carLabel = (car) => {
  if (!car) return '';
  const stock = car.stock || car.objectID || '';
  const desc = [car.year, car.make, car.model].filter(Boolean).join(' ');
  return desc ? `${stock} — ${desc}` : String(stock);
};

// Lender incentives arrive in batches — a statement lists a dozen deals at once.
// This books them all in one pass: each row raises that car's finance invoice and
// records the matching deposit.
export default function BulkIncentiveModal({ open, onClose }) {
  const selectMenuProps = useSelectMenuProps();
  const autocompleteSlotProps = useAutocompleteSlotProps();
  const [account, setAccount] = React.useState('');
  const [rows, setRows] = React.useState([newRow()]);
  const [saving, setSaving] = React.useState(false);
  const [results, setResults] = React.useState(null);

  React.useEffect(() => {
    if (!open) return;
    setAccount('');
    setRows([newRow()]);
    setResults(null);
  }, [open]);

  const updateRow = (key, field, value) =>
    setRows(prev => prev.map(r => r.key === key ? { ...r, [field]: value } : r));

  const addRow = () => setRows(prev => [...prev, newRow()]);
  const removeRow = (key) => setRows(prev => prev.length === 1 ? prev : prev.filter(r => r.key !== key));

  const validRows = rows.filter(r => r.car && r.memo.trim() && parseFloat(r.amount));
  const runningTotal = validRows.reduce((a, c) => a + parseFloat(c.amount), 0);

  const handleSave = async () => {
    if (!account) {
      StateManager.setAlertAndOpen('Pick the receiving bank', 'warning');
      return;
    }
    if (!validRows.length) {
      StateManager.setAlertAndOpen('Every row needs a car, a memo, and an amount', 'warning');
      return;
    }

    setSaving(true);
    const outcomes = [];
    // One car can legitimately appear on more than one row, so the invoice is
    // re-read between rows rather than cached — otherwise the second incentive
    // would overwrite the first.
    for (const row of validRows) {
      const stockNumber = String(row.car.stock || row.car.objectID);
      try {
        const existingInvoice = await fetchActiveFinanceInvoice(stockNumber);
        const result = await applyIncentive({
          stockNumber,
          memo: row.memo,
          amount: parseFloat(row.amount),
          account,
          existingInvoice,
        });
        outcomes.push({
          stockNumber,
          amount: parseFloat(row.amount),
          ok: true,
          createdInvoice: result.createdInvoice,
        });
      } catch (err) {
        console.error(`Incentive failed for ${stockNumber}`, err);
        outcomes.push({ stockNumber, amount: parseFloat(row.amount), ok: false });
      }
    }
    setSaving(false);
    setResults(outcomes);

    const succeeded = outcomes.filter(o => o.ok);
    const failed = outcomes.filter(o => !o.ok);
    if (!failed.length) {
      StateManager.setAlertAndOpen(
        `${succeeded.length} incentive${succeeded.length === 1 ? '' : 's'} posted — ${USD(succeeded.reduce((a, c) => a + c.amount, 0))}`,
        'success',
      );
      onClose();
    } else {
      StateManager.setAlertAndOpen(
        `${succeeded.length} posted, ${failed.length} failed — see the dialog for details`,
        'error',
      );
    }
  };

  return (
    <StackedDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h6" sx={{ lineHeight: 1.2 }}>Add Lender Incentives</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Raises each car's finance invoice and books the deposit
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ mb: 2, maxWidth: 260 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Receiving Bank</InputLabel>
            <Select value={account} onChange={(e) => setAccount(e.target.value)} label="Receiving Bank" MenuProps={selectMenuProps}>
              {StateManager.deposit_banks.map(bank => (
                <MenuItem key={bank.value} value={bank.value}>{bank.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Divider sx={{ mb: 1 }} />

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Car</TableCell>
              <TableCell>Memo</TableCell>
              <TableCell sx={{ width: 140 }}>Amount</TableCell>
              <TableCell sx={{ width: 44 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.key}>
                <TableCell sx={{ py: 0.75, px: 1, minWidth: 260 }}>
                  <CarSearchField
                    value={row.car}
                    onChange={(car) => updateRow(row.key, 'car', car)}
                    slotProps={autocompleteSlotProps}
                    disabled={saving}
                  />
                </TableCell>
                <TableCell sx={{ py: 0.75, px: 1 }}>
                  <TextField
                    fullWidth size="small" placeholder="e.g. Woodside reserve"
                    value={row.memo}
                    onChange={(e) => updateRow(row.key, 'memo', e.target.value)}
                    disabled={saving}
                  />
                </TableCell>
                <TableCell sx={{ py: 0.75, px: 1 }}>
                  <TextField
                    fullWidth size="small" type="number" placeholder="0.00"
                    value={row.amount}
                    onChange={(e) => updateRow(row.key, 'amount', e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                    disabled={saving}
                  />
                </TableCell>
                <TableCell sx={{ py: 0.75, px: 0.5 }}>
                  <IconButton size="small" onClick={() => removeRow(row.key)} disabled={saving || rows.length === 1}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Button startIcon={<AddIcon />} size="small" onClick={addRow} sx={{ mt: 1.5 }} disabled={saving}>
          Add Row
        </Button>

        {results && results.some(r => !r.ok) && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Failed: {results.filter(r => !r.ok).map(r => r.stockNumber).join(', ')}. The rest posted
            successfully — fix these rows and save again.
          </Alert>
        )}
        {results && results.some(r => r.ok && r.createdInvoice) && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Created a new finance invoice for: {results.filter(r => r.ok && r.createdInvoice).map(r => r.stockNumber).join(', ')}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1, ml: 2 }}>
          {validRows.length} incentive{validRows.length === 1 ? '' : 's'} · {USD(runningTotal)}
        </Typography>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || !validRows.length}>
          {saving ? 'Posting…' : 'Post Incentives'}
        </Button>
      </DialogActions>
    </StackedDialog>
  );
}

// Algolia-backed car picker. Debounced so typing a stock number doesn't fire a
// query per keystroke.
function CarSearchField({ value, onChange, slotProps, disabled }) {
  const [input, setInput] = React.useState('');
  const [options, setOptions] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    const query = input.trim();
    if (!query) { setOptions([]); return; }
    let cancelled = false;
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const { hits } = await algolia.client.initIndex('cars').search(query, { hitsPerPage: 8 });
        if (!cancelled) setOptions(hits);
      } catch (err) {
        console.error('Car search failed', err);
        if (!cancelled) setOptions([]);
      }
      if (!cancelled) setLoading(false);
    }, 250);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [input]);

  return (
    <Autocomplete
      size="small"
      value={value}
      options={options}
      filterOptions={(x) => x}
      getOptionLabel={carLabel}
      isOptionEqualToValue={(a, b) => (a.stock || a.objectID) === (b.stock || b.objectID)}
      onChange={(e, selected) => onChange(selected)}
      onInputChange={(e, next, reason) => { if (reason === 'input') setInput(next); }}
      loading={loading}
      disabled={disabled}
      noOptionsText={input.trim() ? 'No cars found' : 'Search by stock number or description'}
      slotProps={slotProps}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Search cars"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={14} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
